/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: GPT-5 Codex
 🏢 Company: Ring2All LLC
 📄 File: CdrRecordsPage.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Main page for Call Detail Records with list view.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useEffect } from 'react';
import { Play, FileX, RefreshCw, FilterX } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { ModulePageContainer } from '../../components/layouts';
import { useNavigationStore } from '../../store/navigation';
import { useCdrRecordList } from './hooks/useCdrRecordList';
import { CdrRecord } from './types';
import { Button } from '../../components/ui/button';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { RecordingPlayer } from './components/RecordingPlayer';
import { CdrDataGrid } from './components/CdrDataGrid';
import FixedActionBar from '../../components/ui/FixedActionBar';

export default function CdrRecordsPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const [selectedRecording, setSelectedRecording] = useState<CdrRecord | null>(null);

  const pageTitle = useMemo(() => t('callDetailRecords.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
  } = useCdrRecordList();

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'startStamp') {
          return {
            ...column,
            render: (_value: unknown, item: CdrRecord) => (
              <span className="text-sm text-foreground">
                {item.startStamp
                  ? new Date(item.startStamp).toLocaleString()
                  : '—'}
              </span>
            )
          };
        }
        if (column.key === 'duration') {
          return {
            ...column,
            render: (_value: unknown, item: CdrRecord) => {
              if (item.duration === null || item.duration === undefined) return '—';
              const minutes = Math.floor(item.duration / 60);
              const seconds = item.duration % 60;
              return (
                <span className="text-sm text-foreground">
                  {minutes}:{seconds.toString().padStart(2, '0')}
                </span>
              );
            }
          };
        }
        if (column.key === 'billsec') {
          return {
            ...column,
            render: (_value: unknown, item: CdrRecord) => {
              if (item.billsec === null || item.billsec === undefined) return '—';
              const minutes = Math.floor(item.billsec / 60);
              const seconds = item.billsec % 60;
              return (
                <span className="text-sm text-foreground">
                  {minutes}:{seconds.toString().padStart(2, '0')}
                </span>
              );
            }
          };
        }
        if (column.key === 'direction') {
          return {
            ...column,
            render: (_value: unknown, item: CdrRecord) => {
              if (!item.direction) return '—';
              return (
                <span className="text-sm font-medium text-foreground">
                  {t(`callDetailRecords.directions.${item.direction}`, { defaultValue: item.direction })}
                </span>
              );
            }
          };
        }
        if (column.key === 'hangupCause') {
          return {
            ...column,
            render: (_value: unknown, item: CdrRecord) => {
              if (!item.hangupCause) return '—';
              return (
                <span className="text-sm text-muted-foreground">
                  {item.hangupCause}
                </span>
              );
            }
          };
        }
        if (column.key === 'recordingFile') {
          return {
            ...column,
            render: (_value: unknown, item: CdrRecord) => {
              if (!item.recordingFile) {
                return (
                  <SimpleTooltip content={t('callDetailRecords.recording.notAvailable')}>
                    <div className="flex items-center justify-center">
                      <FileX className="h-4 w-4 text-muted-foreground/50" />
                    </div>
                  </SimpleTooltip>
                );
              }
              
              return (
                <SimpleTooltip content={t('callDetailRecords.recording.playTooltip')}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setSelectedRecording(item)}
                  >
                    <Play className="h-4 w-4 text-primary" />
                  </Button>
                </SimpleTooltip>
              );
            }
          };
        }
        return column;
      }),
    [baseColumns, t]
  );

  // Check if any filter has been set (para mostrar el botón Clear)
  const hasActiveFilters = !!(
    filters.startDate ||
    filters.endDate ||
    filters.caller ||
    filters.destination ||
    filters.direction
  );

  return (
    <>
      <ModulePageContainer
        currentView="list"
        listView={
          <CdrDataGrid
            data={data}
            columns={columns}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            filters={filters}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            onFilterChange={handleFilterChange}
          />
        }
        formView={null}
      >
        {/* Recording Player Modal */}
        {selectedRecording && selectedRecording.recordingFile && (
          <RecordingPlayer
            cdrId={selectedRecording.id}
            recordingFile={selectedRecording.recordingFile}
            callInfo={{
              caller: selectedRecording.callerIdNumber || 'Unknown',
              destination: selectedRecording.destinationNumber || 'Unknown',
              startTime: selectedRecording.startStamp || '',
            }}
            onClose={() => setSelectedRecording(null)}
          />
        )}
      </ModulePageContainer>

      {/* Fixed Action Bar - Siempre visible */}
      <FixedActionBar
        onReset={hasActiveFilters ? handleClearFilters : undefined}
        onSave={handleApplyFilters}
        resetText={
          <>
            <FilterX className="h-4 w-4 mr-2" />
            {t('callDetailRecords.filters.clear')}
          </>
        }
        saveText={
          <>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('callDetailRecords.filters.refresh')}
          </>
        }
        showCancel={false}
        showDelete={false}
        isSaving={loading}
      />
    </>
  );
}
